/**
 * MenuItemPropertiesPanel — prawa kolumna buildera menu (ADR-0031).
 *
 * Renderuje pola edycyjne dla aktualnie wybranego MenuItem. Per typ inne pola:
 *   - module / system_item — label override + iconName override
 *   - custom_link — url + external (checkbox) + label + iconName
 *   - section — label + iconName + collapsed default
 *   - separator — komunikat „brak pól"
 *   - installed_area — label override + iconName + read-only areaCode (hub Krok 4;
 *     zawartość sekcji pobierana automatycznie z zainstalowanych modułów obszaru)
 *
 * Stan trzymany przez parent (controlled). Emituje pełen MenuItem przez `onChange`.
 */

import { useTranslation } from 'react-i18next';
import type { AdminMenuItem, MenuItem } from '@saas/shared';

import { IconPicker } from '@/components/modules/IconPicker';

export interface MenuItemPropertiesPanelProps {
  item: MenuItem | AdminMenuItem | null;
  onChange: (next: MenuItem | AdminMenuItem) => void;
  /**
   * i18n key prefix (np. 'tenant.menuLayoutBuilder' albo 'admin.menuLayoutBuilder').
   * Default: 'tenant.menuLayoutBuilder' (back-compat).
   */
  i18nPrefix?: string;
  /**
   * Kod szablonu (scope huba) — gdy podany, panel może oferować akcje specyficzne
   * dla szablonu (np. projektowanie widoku listy pozycji menu).
   */
  templateCode?: string;
  /**
   * Callback otwierający builder widoku listy dla wybranej pozycji menu.
   * Pokazywany tylko gdy podany i pozycja ma type='module'.
   */
  onDesignView?: (item: MenuItem) => void;
  /**
   * Callback otwierający builder widoku formularza dla wybranej pozycji menu.
   * Pokazywany tylko gdy podany i pozycja ma type='module'.
   */
  onDesignFormView?: (item: MenuItem) => void;
}

export function MenuItemPropertiesPanel({
  item,
  onChange,
  i18nPrefix = 'tenant.menuLayoutBuilder',
  templateCode: _templateCode,
  onDesignView,
  onDesignFormView,
}: MenuItemPropertiesPanelProps) {
  const { t } = useTranslation();

  if (!item) {
    return (
      <aside
        className="flex w-72 flex-col gap-4 border-l border-border-secondary bg-bg-elevated p-4 overflow-y-auto"
        data-testid="menu-properties-panel"
      >
        <h2 className="text-xs font-semibold uppercase tracking-wider text-text-muted">
          {t(`${i18nPrefix}.properties.header`)}
        </h2>
        <p className="text-xs text-text-muted">{t(`${i18nPrefix}.properties.selectPrompt`)}</p>
      </aside>
    );
  }

  return (
    <aside
      className="flex w-72 flex-col gap-4 border-l border-border-secondary bg-bg-elevated p-4 overflow-y-auto"
      data-testid="menu-properties-panel"
    >
      <h2 className="text-xs font-semibold uppercase tracking-wider text-text-muted">
        {t(`${i18nPrefix}.properties.headerWithType`, {
          type: t(`${i18nPrefix}.type.${item.type}`),
        })}
      </h2>

      {item.type === 'separator' ? (
        <p className="text-xs text-text-muted">{t(`${i18nPrefix}.properties.separatorNoFields`)}</p>
      ) : null}

      {item.type === 'custom_link' ? (
        <Field
          label={t(`${i18nPrefix}.properties.fieldUrl`)}
          required
          testId="props-url"
          input={
            <input
              type="text"
              value={item.url ?? ''}
              onChange={(e) => onChange({ ...item, url: e.target.value })}
              placeholder={t(`${i18nPrefix}.properties.fieldUrlPlaceholder`)}
              className="w-full rounded-md border border-border-secondary bg-bg-primary px-2.5 py-1.5 text-sm"
            />
          }
        />
      ) : null}

      {item.type === 'custom_link' ? (
        <Field
          label={t(`${i18nPrefix}.properties.fieldExternal`)}
          testId="props-external"
          input={
            <label className="flex items-center gap-2 text-sm text-text-secondary">
              <input
                type="checkbox"
                checked={item.external ?? false}
                onChange={(e) => onChange({ ...item, external: e.target.checked })}
              />
              {t(`${i18nPrefix}.properties.fieldExternalHint`)}
            </label>
          }
        />
      ) : null}

      {item.type !== 'separator' ? (
        <Field
          label={t(`${i18nPrefix}.properties.fieldLabel`)}
          required={item.type === 'custom_link' || item.type === 'section'}
          hint={
            item.type === 'module' || item.type === 'system_item' || item.type === 'installed_area'
              ? t(`${i18nPrefix}.properties.fieldLabelHintModule`)
              : undefined
          }
          testId="props-label"
          input={
            <input
              type="text"
              value={item.label ?? ''}
              onChange={(e) => onChange({ ...item, label: e.target.value })}
              placeholder={item.label ?? '—'}
              className="w-full rounded-md border border-border-secondary bg-bg-primary px-2.5 py-1.5 text-sm"
            />
          }
        />
      ) : null}

      {item.type !== 'separator' ? (
        <Field
          label={t(`${i18nPrefix}.properties.fieldIcon`)}
          hint={t(`${i18nPrefix}.properties.fieldIconHint`)}
          testId="props-icon"
          input={
            <IconPicker
              value={item.iconName ?? ''}
              onChange={(name) => onChange({ ...item, iconName: name || undefined })}
            />
          }
        />
      ) : null}

      {item.type === 'section' ? (
        <Field
          label={t(`${i18nPrefix}.properties.fieldCollapsed`)}
          testId="props-collapsed"
          input={
            <label className="flex items-center gap-2 text-sm text-text-secondary">
              <input
                type="checkbox"
                checked={item.collapsed ?? false}
                onChange={(e) => onChange({ ...item, collapsed: e.target.checked })}
              />
              {t(`${i18nPrefix}.properties.fieldCollapsedHint`)}
            </label>
          }
        />
      ) : null}

      {item.type === 'module' ? (
        <Field
          label={t(`${i18nPrefix}.properties.fieldModuleCode`)}
          testId="props-module-code"
          input={
            <input
              type="text"
              value={item.moduleCode ?? ''}
              readOnly
              className="w-full cursor-not-allowed rounded-md border border-border-secondary bg-bg-tertiary px-2.5 py-1.5 text-sm text-text-muted"
            />
          }
        />
      ) : null}

      {item.type === 'module' && onDesignView && item.moduleCode ? (
        <button
          type="button"
          data-testid="props-design-list-view"
          onClick={() => onDesignView(item as MenuItem)}
          className="w-full rounded-md border border-border-secondary bg-bg-primary px-3 py-1.5 text-sm font-medium text-text-primary transition hover:bg-bg-secondary"
        >
          {t('admin.templateMenuBuilder.properties.designListView')}
        </button>
      ) : null}

      {item.type === 'module' && onDesignFormView && item.moduleCode ? (
        <button
          type="button"
          data-testid="props-design-form-view"
          onClick={() => onDesignFormView(item as MenuItem)}
          className="w-full rounded-md border border-border-secondary bg-bg-primary px-3 py-1.5 text-sm font-medium text-text-primary transition hover:bg-bg-secondary"
        >
          {t('admin.templateMenuBuilder.properties.designFormView')}
        </button>
      ) : null}

      {item.type === 'installed_area' ? (
        <Field
          label={t(`${i18nPrefix}.properties.fieldAreaCode`)}
          hint={t(`${i18nPrefix}.properties.installedAreaHint`)}
          testId="props-area-code"
          input={
            <input
              type="text"
              value={item.areaCode ?? ''}
              readOnly
              className="w-full cursor-not-allowed rounded-md border border-border-secondary bg-bg-tertiary px-2.5 py-1.5 text-sm text-text-muted"
            />
          }
        />
      ) : null}

      {item.type === 'system_item' ? (
        <Field
          label={t(`${i18nPrefix}.properties.fieldItemKey`)}
          testId="props-item-key"
          input={
            <input
              type="text"
              value={item.itemKey ?? ''}
              readOnly
              className="w-full cursor-not-allowed rounded-md border border-border-secondary bg-bg-tertiary px-2.5 py-1.5 text-sm text-text-muted"
            />
          }
        />
      ) : null}
    </aside>
  );
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

function Field({
  label,
  required,
  hint,
  input,
  testId,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  input: React.ReactNode;
  testId?: string;
}) {
  return (
    <div className="flex flex-col gap-1" data-testid={testId}>
      <label className="text-xs font-medium text-text-secondary">
        {label}
        {required ? <span className="ml-0.5 text-red-500">*</span> : null}
      </label>
      {input}
      {hint ? <p className="text-[11px] text-text-muted">{hint}</p> : null}
    </div>
  );
}
